import { Injectable } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Basisklasse, damit Formularkomponenten auf der Disclaimerseite Daten laden und speichern können.
 */
@Injectable()
export abstract class IDisclaimerService {
    /**
     * Observable mit den aktuellen Disclaimer-Daten.
     */
    private disclaimers = new ReplaySubject<Disclaimers>(1);

    private formGroup: UntypedFormGroup;

    private current: Disclaimers;

    private formControlBuilders: { [name: string]: (value: any) => UntypedFormControl } = {};

    /**
     * Registriert ein FormControl, dass im Disclaimer-Formular angezeigt werden soll.
     * Der Name entspricht dem FormControlName. Der Builder ist eine Funktion, der den intialen Wert
     * der FormControl entgegennimmt und das FormControl erzeugt.
     */
    registerFormControlBuilder(name: string, builder: (value: any) => UntypedFormControl) {
        this.formControlBuilders[name] = builder;
        // Falls FormGroup schon erstellt wurde, dann direkt hinzufügen.
        // Es wird hier setControl statt addControl verwendet, da in einigen Randfällen
        // die Controls dynamisch neu geladen werden, die FormGroup aber erhalten bleibt.
        // (z.b. Umschalten in AdminModus)
        if (this.formGroup) {
            this.formGroup.setControl(name, builder(this.current.formData[name]));
        }
    }

    getDisclaimers(): Observable<Disclaimers> {
        return this.disclaimers.asObservable();
    }

    /**
     * Erzeugt die FormGroup für das Discclaimer-Formular und fügt die registrierten FormControls hinzu.
     */
    buildForm(): Observable<UntypedFormGroup> {
        return this.getDisclaimers()
            .pipe(map(d => {
                this.formGroup = new UntypedFormGroup({});
                this.current = d;
                for (const name in this.formControlBuilders) {
                    const builder = this.formControlBuilders[name];
                    this.formGroup.addControl(name, builder(this.current.formData[name]));
                }
                return this.formGroup;
            }));
    }

    /** Löscht die FormGroup und die registrierten FormControls. Muss von der verwendenden Komponente im ngOnDestroy aufgerufen werden.  */
    destroyForm() {
        this.formGroup = null;
        this.current = null;
        this.formControlBuilders = {};
    }

    /**
     * Speichert die Disclaimer-Bestätigung und die Daten aud dem Disclaimer-Formular.
     */
    acceptAndSave(disclaimerKey: string, data: any) {
        const disclaimers = this.current.update(disclaimerKey, data);
        this.saveDisclaimerData(disclaimers);
        this.disclaimers.next(disclaimers);
    }

    /**
     * Wird von Unterklassen überschrieben und vom DisclaimerInterceptor genutzt um an die aktuell zu setzenden
     * HTTP-Header zu kommen.
     *
     * @returns {Map<string, string>}
     */
    public getHttpHeadersFromDisclaimerData(): Map<string, string> {
        return new Map<string, string>();
    }

    /** Unterklassen müssen die aufrufen, wenn die Disclaimer-Daten geladen wurden.
     * Der Disclaimer-Service published diese Änderungen dann über sein Observable*/
    protected loaded(disclaimer: Disclaimers) {
        this.disclaimers.next(disclaimer);
    }

    /** Gibt zurück ob das gebaute Formular dirty ist. */
    protected isDirty(): boolean {
        return this.formGroup?.dirty ?? false
    }

    /**
     * Von Unterklassen zu implementieren: Speichern der Disclaimer-Daten.
     */
    protected saveDisclaimerData(_: Disclaimers) {
        // Mit Absicht leer
    }

}

export class Disclaimers {
    constructor(
        // Speicherung der Bestätigung
        public acceptedKeys: string[] = [],
        // Beliebige Daten, die von Komponenten des Disclaimers gespeichert werden können (z.b. Wohnsitzland)
        public formData = {}) {
    }

    isAccepted(key: string) {
        return this.acceptedKeys.indexOf(key) !== -1;
    }

    // Neuen Disclaimer erzeugen aus bisherigem plus der neuen Bestätigung und den Formulardaten.
    // Letztere werden "gemerged" um Teilupdates zu ermöglichen und somit ggf verschiedene Disclaimertypen mit verschiendenen Daten.
    update(acceptedKey: string, data: any) {
        const keys = acceptedKey && this.acceptedKeys.concat(acceptedKey) || this.acceptedKeys;
        return new Disclaimers(keys, Object.assign({}, this.formData, data));
    }
}
